import React, { useState, useEffect, useRef } from "react";
import { Row, Col, Card, message, Divider, Typography, Space, List, Button } from "antd";
import { PieChartOutlined, PrinterOutlined } from "@ant-design/icons";
import { HeaderPage } from "../../../components/headerPage";
import { HeaderTitle } from "../../../components/headerTitle";
import { useNavigate, useParams } from "react-router-dom";
import { getCampaignByIdApi } from "../../../api/formsColaboradoresCampaigns";
import { getReportApi } from "../../../api/formsColaboradoresReleaseForms";
import ReactEcharts from "echarts-for-react";
import { DataLoading } from "../../../components/loading";
import { useReactToPrint } from "react-to-print";

const { Text } = Typography;

const getAlernativesOptions = (alternatives) => {
  if (!alternatives) return [];
  const array_options = [];

  for (const el of alternatives) {
    array_options?.push({
      value: Number(el?.total_respostas),
      name: el?.descricao,
      itemStyle: {
        color: el?.cor ?? null,
      },
    });
  }

  return array_options;
};

export const FormulariosColaboradoresCampanhasRelatorio = () => {
  const navigate = useNavigate();
  const refPrint = useRef();
  const { id_campanhas } = useParams();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [campanha, setCampanha] = useState({});

  useEffect(() => {
    if (id_campanhas) {
      getCampaignById(id_campanhas);
      getReport(id_campanhas);
    }
  }, [id_campanhas]);

  const getCampaignById = async (id) => {
    try {
      const { statusCode, response } = await getCampaignByIdApi(id);
      if (statusCode === 200) {
        if (!response.data) {
          navigate("/formularios-colaboradores/campanhas");
        }
        setCampanha(response.data);
      } else {
        message.error(response.message);
        navigate("/formularios-colaboradores/campanhas");
      }
    } catch (error) {
      message.error("Ops, houve algum erro!");
      navigate("/formularios-colaboradores/campanhas");
    }
  };

  const getReport = async (id) => {
    setLoading(true);
    try {
      const { statusCode, response } = await getReportApi(id);
      if (statusCode === 200) {
        setData(response.data);
      } else {
        message.error(response.message);
      }
    } catch (error) {
      message.error("Ops, houve algum erro!");
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = useReactToPrint({
    content: () => refPrint.current,
  });

  return (
    <Row align="middle" justify="center" className="content-row">
      <Col span={24} align="middle" justify="center">
        <Col xs={24} sm={24} md={24} lg={24} xl={22} xxl={20} align="left">
          <div className="content-item">
            <HeaderPage
              title="Formulários Colaboradores"
              onClickBack={() => navigate("/formularios-colaboradores/campanhas")}
            />
            <HeaderTitle
              title={["Campanhas", "Relatório Geral"]}
              subtitle={`Campanha - ${campanha?.descricao ?? ""}`}
              icon={<PieChartOutlined />}
              loading={loading}
            />
            <Divider />

            {loading && <DataLoading />}
            {!loading && (
              <>
                <div ref={refPrint}>
                  <Card bordered={false} bodyStyle={{ padding: "3%" }}>
                    <Space direction="vertical">
                      <Text className="line-break" style={{ fontSize: 16 }}>
                        <strong>Campanha:</strong> {data?.campanha?.descricao}
                      </Text>
                      <Text className="line-break" style={{ fontSize: 16 }}>
                        <strong>Formulário:</strong> {data?.descricao}
                      </Text>

                      <Text className="line-break" style={{ fontSize: 16 }}>
                        <strong>Parâmetro Avaliado:</strong>{" "}
                        {data?.campanha?.tipo_parametro === 1
                          ? data?.campanha?.colaborador
                          : data?.campanha?.descricao_parametro}
                      </Text>

                      <Text className="line-break" style={{ fontSize: 14 }}>
                        {data?.observacoes ?? ""}
                      </Text>
                    </Space>
                  </Card>

                  {data?.grupos?.map((grupo) => {
                    return (
                      <Card
                        key={grupo.id}
                        title={grupo.descricao}
                        bordered={false}
                        style={{ marginTop: 10 }}
                        bodyStyle={{ padding: "3%" }}>
                        {grupo?.questoes?.map((questao) => {
                          if ([3].includes(questao.tipo)) {
                            return (
                              <div key={questao.id}>
                                <Text strong>{questao.descricao}</Text>
                                <ReactEcharts
                                  className="nonbreak"
                                  style={{ width: "100%", minHeight: 250 }}
                                  option={{
                                    tooltip: {
                                      trigger: "item",
                                    },
                                    legend: {
                                      orient: "vertical",
                                      left: "left",
                                      top: "middle",
                                      textStyle: {
                                        fontSize: 12,
                                      },
                                    },
                                    series: [
                                      {
                                        name: "Respostas",
                                        type: "pie",
                                        radius: ["40%", "70%"],
                                        avoidLabelOverlap: false,
                                        label: {
                                          show: true,
                                          position: "outside",
                                          formatter: "{d}%",
                                        },
                                        emphasis: {
                                          label: {
                                            show: true,
                                            fontSize: 20,
                                            fontWeight: "bold",
                                          },
                                        },
                                        labelLine: {
                                          show: true,
                                        },
                                        data: getAlernativesOptions(questao.alternativas),
                                      },
                                    ],
                                  }}
                                />

                                {questao?.complementos && (
                                  <List
                                    bordered
                                    header="Complementos"
                                    itemLayout="horizontal"
                                    dataSource={questao?.complementos ?? []}
                                    renderItem={(item) => (
                                      <List.Item>
                                        <Text>
                                          <Text strong>{item?.descricao}</Text>: {item?.complemento}
                                        </Text>
                                      </List.Item>
                                    )}
                                  />
                                )}
                                <Divider />
                              </div>
                            );
                          }

                          if ([4].includes(questao.tipo)) {
                            return (
                              <div key={questao.id}>
                                <Text strong>{questao.descricao}</Text>
                                <ReactEcharts
                                  className="nonbreak"
                                  style={{
                                    width: "100%",
                                    height: `${Math.max(
                                      getAlernativesOptions(questao.alternativas).length * 45,
                                      250
                                    )}px`,
                                  }}
                                  option={{
                                    tooltip: {
                                      trigger: "axis",
                                      axisPointer: {
                                        type: "shadow",
                                      },
                                    },
                                    legend: false,
                                    xAxis: {
                                      type: "value",
                                      boundaryGap: [0, 1],
                                    },
                                    yAxis: {
                                      type: "category",
                                      data: questao?.alternativas?.map((item) => item?.descricao),
                                    },
                                    series: [
                                      {
                                        name: "Alternativas",
                                        type: "bar",
                                        data: questao?.alternativas?.map((item) => Number(item?.total_respostas)),
                                        label: {
                                          show: true,
                                          formatter: "{c}",
                                        },
                                        barHeight: 30,
                                        emphasis: {
                                          label: {
                                            show: true,
                                            fontSize: 25,
                                            fontWeight: "bold",
                                          },
                                        },
                                      },
                                    ],
                                  }}
                                />

                                {questao?.complementos && (
                                  <List
                                    bordered
                                    header="Complementos"
                                    itemLayout="horizontal"
                                    dataSource={questao?.complementos ?? []}
                                    renderItem={(item) => (
                                      <List.Item>
                                        <Text>
                                          <Text strong>{item?.descricao}</Text>: {item?.complemento}
                                        </Text>
                                      </List.Item>
                                    )}
                                  />
                                )}
                                <Divider />
                              </div>
                            );
                          }

                          if ([1, 2].includes(questao.tipo)) {
                            return (
                              <div key={questao.id}>
                                <Text strong>{questao.descricao}</Text>
                                <List
                                  bordered
                                  itemLayout="horizontal"
                                  dataSource={questao.respostas}
                                  renderItem={(item) => (
                                    <List.Item>
                                      <Text>{item?.resposta}</Text>
                                    </List.Item>
                                  )}
                                />
                              </div>
                            );
                          }
                        })}
                      </Card>
                    );
                  })}
                </div>

                <Card bordered={false} bodyStyle={{ padding: "2%" }} style={{ marginTop: 10 }}>
                  <Button onClick={handlePrint} size="large" icon={<PrinterOutlined />}>
                    Imprimir
                  </Button>
                </Card>
              </>
            )}
          </div>
        </Col>
      </Col>
    </Row>
  );
};
